import type {
  AnsweredQuestionnaire,
  ConsentStatusUpdateRequest,
  MedicationsResponse,
  SoleraUserResponse,
  UserProfileResponse
} from "@9amhealth/openapi";
import {
  ConsentControllerService,
  ConsentStatusResponse,
  DemoControllerService,
  MedicationsResponseItem,
  ProfileControllerService,
  QuestionnaireControllerService,
  SmsControllerService,
  SoleraControllerService,
  UserProfileControllerService
} from "@9amhealth/openapi";
import { Cubit } from "blac";
import asTemplateValue from "lib/asTemplateValue";
import type { Condition } from "src/constants/conditions";
import {
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7,
  BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8
} from "@9amhealth/shared";
import { PTHW_BINARY_RATING_QUESTIONNAIRE_ID } from "src/constants/misc";
import { PayerAdditionalLegalTextKey, PayerId } from "src/constants/payers";
import { isAnyPartnerAcceptedAuthMethod } from "src/constants/partnerConfig";
import { AuthenticationMethod } from "src/ui/components/WithAuth/WithAuthCubit";
import { ProfileProgramMembership } from "src/constants/programs";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import { extractErrorCode } from "src/lib/errors";
import formatPhoneNumber from "src/lib/formatPhoneNumber";
import reportErrorSentry from "src/lib/reportErrorSentry";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import type { UserData, UserState } from "src/state/UserCubit/UserState";
import {
  apiMiddleware,
  authenticationState,
  healthSyncState,
  programState,
  pushNotificationState,
  subscriptionState,
  tracker
} from "src/state/state";
import { CustomQuestionnaireAnswer } from "../QuestionnaireCubit/QuestionnaireState";
import { StorageController } from "../StorageBloc/StorageBloc";
import { Blac } from "blac-next";
import UserPreferencesCubit from "../UserPreferencesCubit/UserPreferencesCubit";
import AppViewCubit from "../AppViewCubit/AppViewCubit";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

export type ProfileAttributeSelectedGoals =
  | "Get expert medical advice and treatment"
  | "Reduce or prevent adding medications"
  | "Improve my nutrition and create good habits"
  | "Lose weight and optimize my health"
  | "Sleep better and increase my daily energy";

export enum ProfileAttributesKeys {
  programMemberships = "program.memberships",
  programSelectedGoals = "program.user_selected_goals",
  partnerEmployersCurrent = "partner.employers.current",
  labsConditionGroups = "labs.condition-group",
  partnerPayersCurrent = "partner.payers.current",
  medicalConditions = "medical.conditions",
  hasCompletedInitialOrFollowup = "consultation.has-completed-initial-or-followup",
  phoneNumber = "auth.phone-number",
  memberPopulation = "partner.member_population",
  experienceGroup = "cohort.experience-group",
  primaryCondition = "medical.primary-condition-group",
  registeredAt = "auth.registered-at",
  medicationTransferIsInReview = "medicationtransfer.glp1.is-in-review",
  conciergeCare = "care.concierge-care"
}

export interface MedicalCondition {
  name: string;
  condition: {
    name: Condition;
    icd10: string;
  };
  abatement: string | null;
  onset?: string;
  isActive?: boolean;
}

export enum MemberPopulationType {
  DTC = "DTC",
  B2B = "B2B"
}

export interface MemberPopulation {
  name: string;
  type: "DTC" | "B2B";
}

export enum PayerTrait {
  ELIGIBLE_FOR_MEDICAL_BILLING = "ELIGIBLE_FOR_MEDICAL_BILLING",
  SUPPORTS_THIRD_PARTY_LAB_BILLING = "SUPPORTS_THIRD_PARTY_LAB_BILLING",
  REQUIRES_INITIAL_SYNC_VISIT = "REQUIRES_INITIAL_SYNC_VISIT"
}

interface Payer {
  active: boolean;
  start: string;
  end: string;
  partner: {
    id?: string;
    name: string;
    servedEmployerIds: string[];
    traits: PayerTrait[];
  };
  partnerId: string;
}

interface Employer {
  active: boolean;
  partner: {
    id: string;
    name: string;
    traits: [];
  };
  partnerId: string;
  start: string;
  end: string;
}

interface Membership {
  active: boolean;
  start: string;
  end: string;
  program?: ProfileProgramMembership;
}

export enum ComplexConditionGroup {
  HIGH_RISK = "High risk lab group",
  MODERATE_RISK = "Moderate risk lab group",
  LOW_RISK = "Low risk lab group"
}

export interface ProfileAttributes {
  [ProfileAttributesKeys.partnerEmployersCurrent]: Employer;
  [ProfileAttributesKeys.partnerPayersCurrent]: Payer;
  [ProfileAttributesKeys.programMemberships]: Membership[];
  [ProfileAttributesKeys.medicalConditions]: MedicalCondition[];
  [ProfileAttributesKeys.labsConditionGroups]: ComplexConditionGroup;
  [ProfileAttributesKeys.memberPopulation]: MemberPopulation;
  [ProfileAttributesKeys.programSelectedGoals]: {
    name: ProfileAttributeSelectedGoals;
    isPrimary: boolean;
  }[];
  [ProfileAttributesKeys.hasCompletedInitialOrFollowup]: boolean;
  [ProfileAttributesKeys.phoneNumber]: string;
  [ProfileAttributesKeys.experienceGroup]?: string;
  [ProfileAttributesKeys.primaryCondition]?: string;
  [ProfileAttributesKeys.registeredAt]?: string;
  [ProfileAttributesKeys.medicationTransferIsInReview]?: boolean;
  [ProfileAttributesKeys.conciergeCare]?: ConciergeCareAttribute;
}

/** Shape of the `care.concierge-care` profile attribute (matches PMP's `GET_CONCIERGE_CARE`). */
export interface ConciergeCareAttribute {
  prefersConciergeCare: boolean | null;
  hasEmployerWithOptionalConciergeCare: boolean;
}

const DEBUG_TC_SESSION_STORAGE_KEY = "debug-simulate-tc-session";

/** DEBUG ONLY: fake "current payer" attribute used to simulate a Transcarent member. */
const DEBUG_TRANSCARENT_PAYER: Payer = {
  active: true,
  start: "2024-01-01",
  end: "2099-01-01",
  partnerId: PayerId.TRANSCARENT,
  partner: {
    id: PayerId.TRANSCARENT,
    name: "Transcarent",
    servedEmployerIds: [],
    traits: []
  }
};

export default class UserCubit extends Cubit<UserState> {
  /**
   * Incremented whenever the session's user changes so in-flight requests
   * started by a prior user can detect they're stale and skip writing their
   * response into the next user's state.
   */
  private sessionGeneration = 0;

  constructor(initialState?: UserState) {
    super(initialState ?? {});

    onUserSessionReset(this.clearSession);
  }

  /**
   * Drops the previous user's data along with every request cache that would
   * otherwise answer the next user's load with it: the de-duplicated fan-out
   * promises and the one-minute answered-questionnaires cache.
   */
  private readonly clearSession = (): void => {
    this.sessionGeneration++;
    this.profileAttributesRequest = undefined;
    this.consentStatusRequest = undefined;
    this.answeredQuestionnairesRequest = undefined;
    this.deferredUserDataRequest = undefined;
    this.clearAnsweredQuestionnairesCache();
    this.emit({});
  };

  reset = () => {
    this.emitAndCache({});
  };

  emitAndCache = (state: UserState): void => {
    this.emit(state);
  };

  hasAppAccess = (): boolean => {
    const state = authenticationState.state.authenticationStatus;
    const { userData } = this.state;
    if (
      state === "authenticated" &&
      userData &&
      (userData.email === undefined || userData.passwordSet === false)
    ) {
      return false;
    }

    // catch unauthenticated users
    if (state !== "authenticated") {
      return false;
    }

    return true;
  };

  populateUserprofileWithDemoData = async () => {
    // only allow on dev and qa backend
    const backendApiUrl = envVariables.API_BASE_URL;
    if (!backendApiUrl.includes(".dev.") && !backendApiUrl.includes(".qa.")) {
      return;
    }

    try {
      await DemoControllerService.populate();
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  public get isUserRegistered(): boolean {
    return Boolean(this.state.userData?.email);
  }

  public get programMemberships() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.programMemberships
    ];
  }

  public get medicalConditions() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.medicalConditions
    ];
  }

  public get partnerPayer() {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.partnerPayersCurrent
    ];
  }

  public readonly hasProgramMembership = (
    program: ProfileProgramMembership
  ): boolean => {
    return (
      this.programMemberships?.find(
        (p) => p.program === program && p.active
      ) !== undefined
    );
  };

  public readonly setUserInfo = async (): Promise<void> => {
    const userData = await this.loadUserInfo();

    if (userData) {
      tracker.setAlias(userData.analyticsToken);
      this.emitAndCache({ ...this.state, userData });
      void this.loadMedications();
    }
  };

  fetchUserProfile = async (): Promise<UserProfileResponse> =>
    apiMiddleware.cached(
      async () => (await UserProfileControllerService.getUserProfile()).data
    );

  public readonly loadUserInfo = async (): Promise<UserData | undefined> => {
    try {
      const data = await this.fetchUserProfile();

      const userData = {
        email: asTemplateValue(data.profile.email) as string | undefined,
        analyticsToken: asTemplateValue(data.profile.analyticsToken) as string,
        id: data.id,
        emailVerified: asTemplateValue(data.profile.emailVerified) as boolean,
        sessionAuthenticationMethod: asTemplateValue(
          data.profile.sessionAuthenticationMethod
        ) as string,
        passwordSet: Boolean(data.profile.passwordSet),
        mfaActive: Boolean(data.profile.mfaActive)
      } satisfies UserData;

      const finalUserData = this.applyDebugTcUserData(userData);

      this.emitAndCache({
        ...this.state,
        userData: finalUserData
      });

      const appViewState = Blac.getBloc(AppViewCubit);
      appViewState.setPartnerSession();

      return finalUserData;
    } catch (e: unknown) {
      reportErrorSentry(e);
    }
    return undefined;
  };

  public readonly loadSoleraUserInfo = async (): Promise<
    SoleraUserResponse | undefined
  > => {
    try {
      const response = await SoleraControllerService.retrieveUserInfo();
      if (!this.state.userData) {
        return;
      }

      return response.data;
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };

  private readonly loadMedications = async (): Promise<
    MedicationsResponse | undefined
  > => {
    let result: MedicationsResponse | undefined = undefined;
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.medications);
    try {
      const response = await ProfileControllerService.getMedications();
      result = response.data;
      this.emitAndCache({ ...this.state, medications: result });
    } catch (e: unknown) {
      reportErrorSentry(e);
    } finally {
      loadingState.finish(LoadingKey.medications);
    }

    return result;
  };

  private fetchAnsweredQuestionnairesPromise:
    | [number, Promise<AnsweredQuestionnaire[]>]
    | [number, undefined] = [0, undefined];

  clearAnsweredQuestionnairesCache = () => {
    this.fetchAnsweredQuestionnairesPromise = [0, undefined];
  };

  fetchAnsweredQuestionnaires = async (): Promise<AnsweredQuestionnaire[]> => {
    let [cachedAt, promise] = this.fetchAnsweredQuestionnairesPromise;
    const now = Date.now();
    const cacheTime = 1000 * 60 * 1; // 1 minutes
    const cacheValid = now - cachedAt < cacheTime;

    if (!promise || !cacheValid) {
      promise = new Promise((resolve, reject) => {
        QuestionnaireControllerService.getAnsweredQuestionnaires()
          .then((res) => {
            resolve(res?.data);
          })
          .catch((e: unknown) => {
            reject(e as Error);
          });
      });

      cachedAt = now;
      this.fetchAnsweredQuestionnairesPromise = [cachedAt, promise];
    }

    try {
      return await promise;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return [];
    }
  };

  private answeredQuestionnairesRequest?: Promise<
    AnsweredQuestionnaire[] | undefined
  >;

  public readonly loadUserAnsweredQuestionnaires = async (): Promise<
    AnsweredQuestionnaire[] | undefined
  > => {
    if (this.answeredQuestionnairesRequest) {
      return this.answeredQuestionnairesRequest;
    }

    this.answeredQuestionnairesRequest =
      this.fetchAndCacheAnsweredQuestionnaires().finally(() => {
        this.answeredQuestionnairesRequest = undefined;
      });

    return this.answeredQuestionnairesRequest;
  };

  private readonly fetchAndCacheAnsweredQuestionnaires = async (): Promise<
    AnsweredQuestionnaire[] | undefined
  > => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.answeredQuestionnaires);

    try {
      const answeredQuestionnaires = await this.fetchAnsweredQuestionnaires();

      addSentryBreadcrumb(
        "auth",
        `Successfully load user answered questionnaires`
      );

      this.emitAndCache({ ...this.state, answeredQuestionnaires });
    } catch (e: unknown) {
      reportErrorSentry(e);
    } finally {
      loadingState.finish(LoadingKey.answeredQuestionnaires);
    }

    return this.state.answeredQuestionnaires;
  };

  public readonly getBehavioralHealthQuestionnaireAnswers = async () => {
    await this.loadUserAnsweredQuestionnaires();
    const answeredQuestionnaires = this.state.answeredQuestionnaires ?? [];

    const phq = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        BEHAVIORAL_HEALTH_QUESTIONNAIRE_PHQ8
    );

    const phqData =
      phq.length > 0
        ? phq.reduce((latest, current) => {
            return new Date(current.timestamp) > new Date(latest.timestamp)
              ? current
              : latest;
          })
        : undefined;

    const gad = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        BEHAVIORAL_HEALTH_QUESTIONNAIRE_GAD7
    );

    const gadData =
      gad.length > 0
        ? gad.reduce((latest, current) => {
            return new Date(current.timestamp) > new Date(latest.timestamp)
              ? current
              : latest;
          })
        : undefined;

    const phqAnswers = phqData?.answers.json;
    const gadAnswers = gadData?.answers.json;

    return {
      phqAnswers: phqAnswers as CustomQuestionnaireAnswer[],
      gadAnswers: gadAnswers as CustomQuestionnaireAnswer[]
    };
  };

  public readonly getBinaryRatingQuestionnaireAnswers = async (
    subjectId: string
  ) => {
    await this.loadUserAnsweredQuestionnaires();
    const answeredQuestionnaires = this.state.answeredQuestionnaires ?? [];

    const binaryRatingQuestionnaires = answeredQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.questionnaireRef.id ===
        PTHW_BINARY_RATING_QUESTIONNAIRE_ID
    );

    const binaryRating = binaryRatingQuestionnaires.find((questionnaire) =>
      ((questionnaire.answers?.json ?? []) as CustomQuestionnaireAnswer[]).find(
        (answer: CustomQuestionnaireAnswer) =>
          Array.isArray(answer?.fieldValue) &&
          answer?.fieldValue?.find(
            (field) =>
              field?.choiceId === "rating_subject_uid" &&
              field?.value === subjectId
          )
      )
    );

    return (binaryRating?.answers?.json ?? []) as CustomQuestionnaireAnswer[];
  };

  public readonly checkUserHasAnsweredQuestionnaire = async (
    questionnaireId: string,
    loadAll?: boolean
  ): Promise<AnsweredQuestionnaire | undefined> => {
    if (loadAll !== false) {
      await this.loadUserAnsweredQuestionnaires();
    }

    const allQuestionnaires = this.state.answeredQuestionnaires ?? [];
    const sortedQuestionnaires = allQuestionnaires.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const thisQuestionnaire = sortedQuestionnaires.find(
      (q) => q.questionnaireRef.id === questionnaireId
    );

    return thisQuestionnaire;
  };

  /**
   * The only two calls an app access decision depends on. Everything else the
   * app needs after login is fired un-awaited by loadDeferredUserData.
   */
  readonly loadAppAccess = async (): Promise<{
    profileLoaded: boolean;
    subscriptionsLoaded: boolean;
  }> => {
    apiMiddleware.clearAll();

    const [userData, subscriptionsLoaded] = await Promise.all([
      this.loadUserInfo(),
      subscriptionState.loadAllSubscriptions()
    ]);

    if (userData) {
      tracker.identifyUserByTrackingId(userData.analyticsToken);
    }

    return {
      profileLoaded: Boolean(userData),
      subscriptionsLoaded
    };
  };

  private deferredUserDataRequest?: Promise<void>;

  /**
   * Post-login data that no access decision depends on. Fires and forgets so
   * login never waits on it. Safe to call repeatedly - access check retries
   * share the in-flight fan-out instead of duplicating every request.
   */
  readonly loadDeferredUserData = (): void => {
    const userPreferences = Blac.getBloc(UserPreferencesCubit);

    this.deferredUserDataRequest ??= (async () => {
      try {
        if (this.state.userData) {
          void pushNotificationState.init();
        }

        void userPreferences.setupUserProperties();

        const profileAttributes = await this.ensureProfileAttributesLoaded();

        // Sequenced after getProfileAttributes so it doesn't read profile
        // attributes that a racing call is still writing (first-login
        // "no programs detected" bug).
        void programState.checkUserProfileProgramData();

        subscriptionState.checkForCoupon();

        if (StorageController.getItem("healthActive") === "true") {
          void healthSyncState.checkAndSync();
        }

        if (profileAttributes) {
          this.emitAndCache({ ...this.state, profileAttributes });
        }
      } catch (e: unknown) {
        reportErrorSentry(e);
      }
    })().finally(() => {
      this.deferredUserDataRequest = undefined;
    });
  };

  static setNotificationNumber = async (
    phoneNumber: string,
    options: {
      showLoading?: boolean;
    } = {}
  ): Promise<{ error: string }> => {
    apiMiddleware.clearAll();
    let error = "";
    const loadingState = Blac.getBloc(LoadingCubit);
    try {
      if (options.showLoading !== false) {
        loadingState.start(LoadingKey.treatmentPlan);
      }
      const number = formatPhoneNumber(phoneNumber);

      if (number) {
        await SmsControllerService.registerPhoneNumber({
          phoneNumber: number,
          verify: false
        });
      }
      addSentryBreadcrumb("auth", `Successfully set notification number`);
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = extractErrorCode(e);
    } finally {
      if (options.showLoading !== false) {
        loadingState.finish(LoadingKey.treatmentPlan);
      }
    }
    return { error };
  };

  private getUserMedication(
    medication: MedicationsResponseItem.medication
  ): MedicationsResponseItem | undefined {
    const medicationList = this.state.medications?.medications ?? [];
    return medicationList.find((item) => item.medication === medication);
  }

  get userIsOnInsulin(): boolean {
    const insulin = this.getUserMedication(
      MedicationsResponseItem.medication.INSULIN
    );
    return Boolean(insulin);
  }

  getProfileAttributes = async (
    attributes: ProfileAttributesKeys[] = [
      ProfileAttributesKeys.phoneNumber,
      ProfileAttributesKeys.partnerEmployersCurrent,
      ProfileAttributesKeys.partnerPayersCurrent,
      ProfileAttributesKeys.programMemberships,
      ProfileAttributesKeys.medicalConditions,
      ProfileAttributesKeys.labsConditionGroups,
      ProfileAttributesKeys.programSelectedGoals,
      ProfileAttributesKeys.memberPopulation,
      ProfileAttributesKeys.experienceGroup,
      ProfileAttributesKeys.hasCompletedInitialOrFollowup,
      ProfileAttributesKeys.primaryCondition,
      ProfileAttributesKeys.registeredAt,
      ProfileAttributesKeys.conciergeCare
    ]
  ): Promise<ProfileAttributes | undefined> => {
    const generation = this.sessionGeneration;
    try {
      const res =
        await ProfileControllerService.getMyProfileAttributeValues(attributes);
      const data = this.applyDebugTcProfileAttributes(
        res.data as unknown as ProfileAttributes
      ) as ProfileAttributes;
      if (generation !== this.sessionGeneration) {
        return undefined;
      }
      this.emit({
        ...this.state,
        profileAttributes: data
      });
      return data;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return undefined;
    }
  };

  private profileAttributesRequest?: Promise<ProfileAttributes | undefined>;

  readonly ensureProfileAttributesLoaded = async (
    required: ProfileAttributesKeys[] = []
  ): Promise<ProfileAttributes | undefined> => {
    const current = this.state.profileAttributes;
    if (current && required.every((key) => key in current)) {
      return current;
    }

    this.profileAttributesRequest ??= this.getProfileAttributes().finally(
      () => {
        this.profileAttributesRequest = undefined;
      }
    );

    return this.profileAttributesRequest;
  };

  fetchAndMergeProfileAttributes = async (
    attributes: ProfileAttributesKeys[]
  ): Promise<ProfileAttributes | undefined> => {
    try {
      const res =
        await ProfileControllerService.getMyProfileAttributeValues(attributes);
      const data = res.data as unknown as Partial<ProfileAttributes>;
      const merged = {
        ...this.state.profileAttributes,
        ...data
      } as ProfileAttributes;
      this.emit({
        ...this.state,
        profileAttributes: merged
      });
      return merged;
    } catch (e: unknown) {
      reportErrorSentry(e);
      return undefined;
    }
  };

  checkProfileAttributesCurrentProgramMembership = async (): Promise<
    ProfileProgramMembership[] | false
  > => {
    const data =
      this.state.profileAttributes?.[ProfileAttributesKeys.programMemberships];

    // check if data exists
    if (typeof data === "undefined" || data.length === 0) {
      return false;
    }

    //   collect programs
    const programs: ProfileProgramMembership[] = [];
    data.forEach((item) => {
      if (item.active && item.program) {
        programs.push(item.program);
      }
    });

    return programs;
  };

  public readonly setProgramMemberships = (
    program: ProfileProgramMembership
  ) => {
    if (this.state.profileAttributes) {
      this.emit({
        ...this.state,
        profileAttributes: {
          ...this.state.profileAttributes,
          [ProfileAttributesKeys.programMemberships]: [
            {
              active: true,
              start: "",
              end: "",
              program
            }
          ]
        }
      });
    }
  };

  /******************** */
  // Consent Management //
  /******************** */

  shouldIgnoreConsentUpdate = (consent: ConsentStatusResponse) => {
    const ignoreConsentTypes = [
      ConsentStatusResponse.consent.HEALTH_INFORMATION_RELEASE
    ];
    const ignoreConsentVersions: Partial<
      Record<ConsentStatusResponse.consent, number>
    > = {
      [ConsentStatusResponse.consent.TERMS_OF_SERVICE]: 20240903
    };

    const ignoreConsent = ignoreConsentTypes.includes(consent.consent);
    const ignoreConsentVersion =
      ignoreConsentVersions[consent.consent] === consent.version;

    return ignoreConsent || ignoreConsentVersion;
  };

  getConsentStatus = async (): Promise<void> => {
    const generation = this.sessionGeneration;
    try {
      const result = await ConsentControllerService.listConsentStatuses();
      const consentStatus = result.data;
      if (generation !== this.sessionGeneration) {
        return;
      }
      this.emitAndCache({
        ...this.state,
        consentStatus
      });
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  private consentStatusRequest?: Promise<void>;

  readonly ensureConsentStatusLoaded = async (): Promise<void> => {
    if (this.state.consentStatus !== undefined) {
      return;
    }

    this.consentStatusRequest ??= this.getConsentStatus().finally(() => {
      this.consentStatusRequest = undefined;
    });

    return this.consentStatusRequest;
  };

  acceptConsents = async (): Promise<void> => {
    if (
      this.state.consentStatus === undefined ||
      this.state.consentStatus.length === 0
    ) {
      await this.getConsentStatus();
    }

    const newStatuses = this.state.consentStatus?.map((c) => ({
      ...c,
      status: ConsentStatusResponse.status.ACCEPTED
    }));

    try {
      await ConsentControllerService.updateConsentStatus(
        newStatuses as ConsentStatusUpdateRequest[]
      );
      this.emitAndCache({
        ...this.state,
        consentStatus: newStatuses
      });
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };

  get hasConsentsNotAccepted(): boolean | undefined {
    if (typeof this.state.consentStatus === "undefined") {
      return undefined;
    }

    const consents = this.state.consentStatus.filter(
      (con) => !this.shouldIgnoreConsentUpdate(con)
    );

    const justStatus = consents.map((c) => c.status);
    const allAccepted = justStatus.every(
      (s) => s === ConsentStatusResponse.status.ACCEPTED
    );

    return !allAccepted;
  }

  /**
   * True only when the session auth method is explicitly ANONYMOUS. Determined
   * purely from the session auth method (the source of truth from `/me`) — it
   * does not infer anything from email/password state.
   */
  get isAnonymousSession(): boolean {
    return (
      this.state.userData?.sessionAuthenticationMethod ===
      AuthenticationMethod.ANONYMOUS
    );
  }

  /**
   * True when the user authenticated via an external SSO provider (Google or a
   * SAML/Transcarent identity provider). Based on the session auth method
   * rather than inferring from email-verified + no-password state.
   */
  get userIsSso(): boolean {
    const method = this.state.userData?.sessionAuthenticationMethod;
    return (
      method === AuthenticationMethod.GOOGLE ||
      isAnyPartnerAcceptedAuthMethod(method)
    );
  }

  get partnerId(): string | undefined {
    return this.state.profileAttributes?.["partner.payers.current"]?.partnerId;
  }

  get isTranscarentMember(): boolean {
    return Boolean(this.partnerId === PayerId.TRANSCARENT);
  }

  private get debugTcSessionEnabled(): boolean {
    try {
      return sessionStorage.getItem(DEBUG_TC_SESSION_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  /**
   * DEBUG: force a Transcarent SSO login shape onto userData when the debug flag
   * is on. A real SSO member arrives with a verified email (from the IdP) and no
   * password, so we seed both for realism alongside the SSO session auth method.
   */
  private applyDebugTcUserData(userData: UserData): UserData {
    if (!this.debugTcSessionEnabled) {
      return userData;
    }
    return {
      ...userData,
      email: userData.email ?? "transcarent-debug@9am.health",
      emailVerified: true,
      sessionAuthenticationMethod: AuthenticationMethod.TRANSCARENT_SSO,
      passwordSet: false
    };
  }

  /** DEBUG: inject the Transcarent payer attribute when the debug flag is on. */
  private applyDebugTcProfileAttributes(
    attributes?: ProfileAttributes
  ): ProfileAttributes | undefined {
    if (!this.debugTcSessionEnabled) {
      return attributes;
    }
    return {
      ...(attributes ?? {}),
      [ProfileAttributesKeys.partnerPayersCurrent]: DEBUG_TRANSCARENT_PAYER
    } as ProfileAttributes;
  }

  /**
   * DEBUG ONLY: simulate a member authenticated via Transcarent SSO + OTT
   * exchange, without real SSO or response overrides. Persists a sessionStorage
   * flag so the override is re-applied on every profile/attribute load (survives
   * reload). Does NOT seed name preferences.
   *
   * enable  -> set flag + apply override to current in-memory state.
   * disable -> clear flag + refetch real profile/attributes to restore.
   */
  simulateTranscarentSession = (enabled: boolean): void => {
    try {
      if (enabled) {
        sessionStorage.setItem(DEBUG_TC_SESSION_STORAGE_KEY, "true");
      } else {
        sessionStorage.removeItem(DEBUG_TC_SESSION_STORAGE_KEY);
      }
    } catch {
      // sessionStorage unavailable — best-effort only
    }

    if (enabled) {
      this.emit({
        ...this.state,
        userData: this.state.userData
          ? this.applyDebugTcUserData(this.state.userData)
          : this.state.userData,
        profileAttributes: this.applyDebugTcProfileAttributes(
          this.state.profileAttributes
        )
      });
    } else {
      void this.loadUserInfo();
      void this.getProfileAttributes();
    }
  };

  get isStateOfGeorgiaMember(): boolean {
    return Boolean(
      this.state.profileAttributes?.["partner.payers.current"]?.partnerId ===
      PayerId.GEORGIA
    );
  }

  isPayerMember(payerId: string): boolean {
    return (
      this.state.profileAttributes?.["partner.payers.current"]?.partnerId ===
      payerId
    );
  }

  isEmployerMember(employerId: string): boolean {
    return (
      this.state.profileAttributes?.["partner.employers.current"]?.partnerId ===
      employerId
    );
  }

  get payerAdditionalLegalTextKey() {
    const payerId = this.partnerId as PayerId | undefined;
    return payerId ? PayerAdditionalLegalTextKey[payerId] : undefined;
  }

  get labsConditionGroup(): ComplexConditionGroup | undefined {
    return this.state.profileAttributes?.[
      ProfileAttributesKeys.labsConditionGroups
    ];
  }
}
